import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Link,
} from '@chakra-ui/react';
import {
  alumbradoApi, localidadesApi, TIPOS_VIALIDAD, Localidad, ReporteAlumbrado,
} from '../../../api/serviciosApi';
import { useToast } from '@chakra-ui/react';
import MapSelector from '../../../components/MapSelector';
import EvidenciasInline from '../EvidenciasInline';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const ORIGENES = ['WhatsApp', 'Manual', 'Oficio', 'PortalCiudadano'];
const ESTATUS = ['Pendiente', 'En proceso', 'Atendido', 'Cancelado'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const num = (v: string) => (v === '' ? undefined : Number(v));
const dateOnly = (v?: string) => (v ? v.split('T')[0] : '');

const FormularioAlumbrado: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [folio, setFolio] = useState(data?.folio || '');
  const [fecha, setFecha] = useState(dateOnly(data?.fecha) || new Date().toISOString().split('T')[0]);
  const [origenReporte, setOrigenReporte] = useState(data?.origenReporte || 'Manual');
  const [ciudadanoNombre, setCiudadanoNombre] = useState(data?.ciudadanoNombre || '');
  const [ciudadanoApellidos, setCiudadanoApellidos] = useState(data?.ciudadanoApellidos || '');
  const [ciudadanoTelefono, setCiudadanoTelefono] = useState(data?.ciudadanoTelefono || '');
  const [ciudadanoCelular, setCiudadanoCelular] = useState(data?.ciudadanoCelular || '');
  const [ciudadanoCorreo, setCiudadanoCorreo] = useState(data?.ciudadanoCorreo || '');
  const [estado, setEstado] = useState(data?.estado || 'Tlaxcala');
  const [municipio, setMunicipio] = useState(data?.municipio || 'Totolac');
  const [localidad, setLocalidad] = useState(data?.localidad || '');
  const [colonia, setColonia] = useState(data?.colonia || '');
  const [codigoPostal, setCodigoPostal] = useState(data?.codigoPostal || '');
  const [tipoVialidad, setTipoVialidad] = useState(data?.tipoVialidad || '');
  const [vialidad, setVialidad] = useState(data?.vialidad || '');
  const [numeroExterior, setNumeroExterior] = useState(data?.numeroExterior || '');
  const [numeroInterior, setNumeroInterior] = useState(data?.numeroInterior || '');
  const [referencias, setReferencias] = useState(data?.referencias || '');
  const [latitud, setLatitud] = useState(data?.latitud != null ? String(data.latitud) : '');
  const [longitud, setLongitud] = useState(data?.longitud != null ? String(data.longitud) : '');
  const [urlMaps, setUrlMaps] = useState(data?.urlMaps || '');
  const [tipo, setTipo] = useState(data?.tipo || '');
  const [descripcion, setDescripcion] = useState(data?.descripcion || '');
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [fechaAtencion, setFechaAtencion] = useState(dateOnly(data?.fechaAtencion));
  const [actividadesRealizadas, setActividadesRealizadas] = useState(data?.actividadesRealizadas || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'Pendiente');

  useEffect(() => {
    localidadesApi.list().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  const handleGuardar = async () => {
    if (!fecha || !origenReporte) {
      toast({ title: 'Campos obligatorios', description: 'La fecha y el origen del reporte son requeridos.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const body: Partial<ReporteAlumbrado> = {
        folio: folio || undefined, fecha, origenReporte,
        ciudadanoNombre, ciudadanoApellidos, ciudadanoTelefono, ciudadanoCelular, ciudadanoCorreo,
        estado, municipio, localidad, colonia, codigoPostal, tipoVialidad, vialidad,
        numeroExterior, numeroInterior, referencias,
        latitud: num(latitud), longitud: num(longitud), urlMaps,
        tipo, descripcion, responsable,
        fechaAtencion: fechaAtencion || undefined, actividadesRealizadas, estatus,
      };
      if (modo === 'edit') await alumbradoApi.update(data.id, body);
      else await alumbradoApi.create(body);
      toast({ title: modo === 'edit' ? 'Reporte actualizado' : 'Reporte registrado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar el reporte.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');

  if (modo === 'view') {
    const d = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Información General</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FOLIO" value={d.folio} />
          <Field label="FECHA" value={fmt(d.fecha)} />
          <Field label="ORIGEN DEL REPORTE" value={<Badge>{d.origenReporte}</Badge>} />
          <Field label="ESTATUS" value={<Badge>{d.estatus}</Badge>} />
        </SimpleGrid>

        <SectionTitle>Datos del Ciudadano</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="NOMBRE" value={d.ciudadanoNombre} />
          <Field label="APELLIDOS" value={d.ciudadanoApellidos} />
          <Field label="TELÉFONO" value={d.ciudadanoTelefono} />
          <Field label="CELULAR" value={d.ciudadanoCelular} />
          <Field label="CORREO" value={d.ciudadanoCorreo} />
        </SimpleGrid>

        <SectionTitle>Ubicación</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="ESTADO" value={d.estado} />
          <Field label="MUNICIPIO" value={d.municipio} />
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="COLONIA" value={d.colonia} />
          <Field label="CÓDIGO POSTAL" value={d.codigoPostal} />
          <Field label="VIALIDAD" value={d.tipoVialidad ? `${d.tipoVialidad} ${d.vialidad || ''}` : d.vialidad} />
          <Field label="NÚMERO EXT / INT" value={`${d.numeroExterior || '—'} / ${d.numeroInterior || '—'}`} />
          <Field label="REFERENCIAS" value={d.referencias} />
          <Field label="UBICACIÓN" value={d.latitud != null && d.longitud != null ? (
            <Link color="blue.500" isExternal href={d.urlMaps || `https://www.google.com/maps?q=${d.latitud},${d.longitud}`}>Ver en Google Maps</Link>
          ) : undefined} />
        </SimpleGrid>

        <SectionTitle>Detalle del Reporte</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="TIPO" value={d.tipo} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Field label="FECHA DE ATENCIÓN" value={fmt(d.fechaAtencion)} />
        </SimpleGrid>
        {d.descripcion && (<><Text fontSize="xs" color="gray.500" fontWeight="bold" mt={2}>DESCRIPCIÓN</Text><Text fontSize="sm" whiteSpace="pre-wrap">{d.descripcion}</Text></>)}
        {d.actividadesRealizadas && (<><Text fontSize="xs" color="gray.500" fontWeight="bold" mt={2}>ACTIVIDADES REALIZADAS</Text><Text fontSize="sm" whiteSpace="pre-wrap">{d.actividadesRealizadas}</Text></>)}

        <EvidenciasInline archivos={d.archivos} />
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Información General</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Folio</FormLabel>
          <Input placeholder="Auto / manual" value={folio} onChange={(e) => setFolio(e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Origen del reporte</FormLabel>
          <Select value={origenReporte} onChange={(e) => setOrigenReporte(e.target.value)}>
            {ORIGENES.map((o) => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Datos del Ciudadano</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl><FormLabel fontSize="sm">Nombre</FormLabel><Input value={ciudadanoNombre} onChange={(e) => setCiudadanoNombre(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Apellidos</FormLabel><Input value={ciudadanoApellidos} onChange={(e) => setCiudadanoApellidos(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Teléfono</FormLabel><Input value={ciudadanoTelefono} onChange={(e) => setCiudadanoTelefono(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Celular</FormLabel><Input value={ciudadanoCelular} onChange={(e) => setCiudadanoCelular(e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Correo</FormLabel><Input type="email" value={ciudadanoCorreo} onChange={(e) => setCiudadanoCorreo(e.target.value)} /></FormControl>
      </SimpleGrid>

      <SectionTitle>Ubicación</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl><FormLabel fontSize="sm">Estado</FormLabel><Input value={estado} onChange={(e) => setEstado(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Municipio</FormLabel><Input value={municipio} onChange={(e) => setMunicipio(e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl><FormLabel fontSize="sm">Colonia</FormLabel><Input value={colonia} onChange={(e) => setColonia(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Código postal</FormLabel><Input value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de vialidad</FormLabel>
          <Select placeholder="Selecciona" value={tipoVialidad} onChange={(e) => setTipoVialidad(e.target.value)}>
            {TIPOS_VIALIDAD.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Vialidad (nombre)</FormLabel><Input value={vialidad} onChange={(e) => setVialidad(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Núm. exterior</FormLabel><Input value={numeroExterior} onChange={(e) => setNumeroExterior(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Núm. interior</FormLabel><Input value={numeroInterior} onChange={(e) => setNumeroInterior(e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 3' }}><FormLabel fontSize="sm">Referencias</FormLabel><Input value={referencias} onChange={(e) => setReferencias(e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Ubicación en el mapa</FormLabel>
          <MapSelector
            initialLat={latitud ? Number(latitud) : null}
            initialLng={longitud ? Number(longitud) : null}
            onLocationSelect={(lat, lng, address) => {
              setLatitud(String(lat));
              setLongitud(String(lng));
              setUrlMaps(`https://www.google.com/maps?q=${lat},${lng}`);
              if (address?.neighborhood && !colonia) setColonia(address.neighborhood);
              if (address?.postalCode && !codigoPostal) setCodigoPostal(address.postalCode);
            }}
          />
          {latitud && longitud && (
            <Link fontSize="sm" color="blue.500" isExternal mt={1} display="inline-block"
              href={urlMaps || `https://www.google.com/maps?q=${latitud},${longitud}`}>
              Ver en Google Maps ({latitud}, {longitud})
            </Link>
          )}
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Detalle del Reporte</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl><FormLabel fontSize="sm">Tipo</FormLabel><Input placeholder="Ej. Luminaria fundida" value={tipo} onChange={(e) => setTipo(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Responsable</FormLabel><Input value={responsable} onChange={(e) => setResponsable(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Fecha de atención</FormLabel><Input type="date" value={fechaAtencion} onChange={(e) => setFechaAtencion(e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            {ESTATUS.map((e) => <option key={e} value={e}>{e}</option>)}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Descripción</FormLabel><Textarea rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Actividades realizadas</FormLabel><Textarea rows={3} value={actividadesRealizadas} onChange={(e) => setActividadesRealizadas(e.target.value)} /></FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioAlumbrado;
